import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import { AreaSelectorPlaceholderResource } from "api/resource/area-selector-placeholder";
import { AreaSelectorPlaceholder, NewAreaSelectorPlaceholder } from "model/area-selector-placeholder";
import { arrayToById, objectToById } from "redux/normalize";
import { loginReset } from "./authentication";

export interface AreaSelectorPlaceholdersState {
	loading?: boolean;
	error?: string | null;
	byId: {
		[key: number]: AreaSelectorPlaceholderResource;
	};
}

const initialState: AreaSelectorPlaceholdersState = {
	byId: {},
};

// Async thunks
const fetchAreaSelectorPlaceholders = createAsyncThunk(
	"areaSelectorPlaceholders/fetch",
	async (payload, thunkAPI) => {
		thunkAPI.dispatch(areaSelectorPlaceholdersLoading());
		const result = api.getAreaSelectorPlaceholders();
		result
			.then((placeholders) => thunkAPI.dispatch(areaSelectorPlaceholdersLoaded(placeholders)))
			.catch(error => thunkAPI.dispatch(areaSelectorPlaceholdersFailed(error)));
		return result;
	}
);

const createAreaSelectorPlaceholder = createAsyncThunk(
	"areaSelectorPlaceholders/create",
	async (payload: { placeholder: NewAreaSelectorPlaceholder }, thunkAPI) => {
		thunkAPI.dispatch(areaSelectorPlaceholdersLoading());
		const result = api.createAreaSelectorPlaceholder(payload.placeholder);
		result
			.then(placeholder => thunkAPI.dispatch(areaSelectorPlaceholderLoaded(placeholder)))
			.catch(error => thunkAPI.dispatch(areaSelectorPlaceholdersFailed(error)));
		return result;
	}
);

const updateAreaSelectorPlaceholder = createAsyncThunk(
	"areaSelectorPlaceholders/update",
	async (payload: { placeholder: AreaSelectorPlaceholder }, thunkAPI) => {
		thunkAPI.dispatch(areaSelectorPlaceholdersLoading());
		const result = api.updateAreaSelectorPlaceholder(payload.placeholder);
		result
			.then(placeholder => thunkAPI.dispatch(areaSelectorPlaceholderLoaded(placeholder)))
			.catch(error => thunkAPI.dispatch(areaSelectorPlaceholdersFailed(error)));
		return result;
	}
);

const deleteAreaSelectorPlaceholder = createAsyncThunk(
	"areaSelectorPlaceholders/delete",
	async (payload: { id: number }, thunkAPI) => {
		thunkAPI.dispatch(areaSelectorPlaceholdersLoading());
		const result = api.deleteAreaSelectorPlaceholder(payload.id);
		result
			.then(() => thunkAPI.dispatch(areaSelectorPlaceholderDeleted(payload.id)))
			.catch(error => thunkAPI.dispatch(areaSelectorPlaceholdersFailed(error)));
		return result;
	}
);

// Slice
export const areaSelectorPlaceholdersSlice = createSlice({
	name: "areaSelectorPlaceholders",
	initialState,
	reducers: {
		areaSelectorPlaceholdersLoading: (state) => {
			state.loading = true;
		},
		areaSelectorPlaceholdersLoaded: (state, action: PayloadAction<AreaSelectorPlaceholderResource[]>) => {
			state.loading = false;
			state.error = null;
			state.byId = arrayToById(action.payload, "id");
		},
		areaSelectorPlaceholderLoaded: (state, action: PayloadAction<AreaSelectorPlaceholderResource>) => {
			const byId = objectToById(action.payload, "id");
			state.loading = false;
			state.error = null;
			state.byId = { ...state.byId, ...byId };
		},
		areaSelectorPlaceholderDeleted: (state, action: PayloadAction<number>) => {
			state.loading = false;
			state.error = null;
			const { [action.payload]: deleted, ...remaining } = state.byId;
			state.byId = remaining;
		},
		areaSelectorPlaceholdersFailed: (state, action: PayloadAction<any>) => {
			state.loading = false;
			state.error = action.payload.message;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	areaSelectorPlaceholdersLoading,
	areaSelectorPlaceholdersLoaded,
	areaSelectorPlaceholderLoaded,
	areaSelectorPlaceholderDeleted,
	areaSelectorPlaceholdersFailed,
} = areaSelectorPlaceholdersSlice.actions;

export {
	fetchAreaSelectorPlaceholders,
	createAreaSelectorPlaceholder,
	updateAreaSelectorPlaceholder,
	deleteAreaSelectorPlaceholder,
};
